import { useEffect, useMemo, useState } from "react";

import ContactsModal from "../../components/ContactsModal/ContactsModal";
import Footer from "../../components/Footer/Footer";
import Header from "../../components/Header/Header";
import ScrollToTopButton from "../../components/ScrollToTopButton/ScrollToTopButton";
import { useSiteData } from "../../contexts/SiteDataContext";
import type { RatingCategory } from "../../types";
import { normalizeString } from "../../utils/searchUtils";
import { mergeGoodTiers, weightedShuffle } from "../../utils/vpnUtils";
import RatingPageTabSwitch from "./RatingPageTabSwitch/RatingPageTabSwitch";
import RatingPageVpns from "./RatingPageVpns/RatingPageVpns";

function RatingPage() {
  const { siteData } = useSiteData();

  const { ratingList, siteConfig } = siteData;

  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery && window.umami) {
        window.umami.track("Search", { query: searchQuery });
      }
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  // Silver + bronze collapse into one section before anything else looks at
  // the list, so the tab bar and the sections stay in step and search filters
  // the merged group rather than two halves of it.
  //
  // The merged list is reshuffled here rather than served pre-mixed: the API
  // shuffles each RatingType on its own, so concatenating them would put every
  // silver service above every bronze one. Weights come from the admin
  // (`vpn_sorting`), keeping this draw identical to the server's.
  //
  // Memoised on the fetched list, so the order is rolled once per load and
  // typing in the search box never reshuffles the section under the reader.
  const sortingConfig = siteConfig?.vpn_sorting;

  const displayRatingList = useMemo(
    (): RatingCategory[] =>
      mergeGoodTiers(ratingList ?? [], (vpns) =>
        weightedShuffle(vpns, sortingConfig),
      ),
    [ratingList, sortingConfig],
  );

  const filteredRatingList = useMemo((): RatingCategory[] => {
    if (!searchQuery.trim()) {
      return displayRatingList;
    }

    const normalizedQuery = normalizeString(searchQuery);

    return displayRatingList
      .map((rating) => ({
        ...rating,
        vpns: (rating.vpns ?? []).filter((vpn) =>
          normalizeString(vpn.name).includes(normalizedQuery),
        ),
      }))
      .filter((rating) => rating.vpns.length > 0);
  }, [displayRatingList, searchQuery]);

  if (!ratingList) return;

  return (
    <div className="bg-main min-h-screen flex flex-col">
      <Header searchQuery={searchQuery} setSearchQuery={setSearchQuery} />

      <RatingPageTabSwitch
        ratingList={displayRatingList}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />

      <RatingPageVpns
        ratingList={filteredRatingList}
        searchQuery={searchQuery}
      />

      <Footer />
      <ContactsModal />
      <ScrollToTopButton />
    </div>
  );
}

export default RatingPage;
